import React, { useEffect, useState } from 'react';
import { URLS } from "@/lib/urls";
import AdminLayout from "@/components/layout/admin/adminLayout";
import { refundApi } from "@/api/refund";
import {
    Alert,
    Button,
    Card,

    Form,
    Input,
    InputNumber,
    Spin,
    Typography,
} from "antd";
import {
    CalendarDays,
    CheckCircle2,
    FileText,
    Layers3,
    LoaderCircle,
    Plus,
    RotateCcw,
} from "lucide-react";
import {DatePicker} from 'antd-jalali';
import dayjs from "dayjs";

import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";
import {sprintf} from "@/lib/utils/sprintf";
import {useNavigate} from "react-router-dom";
const { Text, Title } = Typography;
const { TextArea } = Input;

function AdminRefoundGroupCreate() {
    const [form] = Form.useForm();

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [hasCalculated, setHasCalculated] = useState(false);
    const [groupTitle, setGroupTitle] = useState("");
    const [description, setDescription] = useState("");

    const navigate = useNavigate()

    const {
        mutate: createNewGroup,
        isPending: isCreating,
    } = refundApi.useAdminCreateGroup();

    const {
        data: calculateData,
        isPending: isPendingCalculate,
    } = refundApi.useAdminCalculateGroupBeforeCreate({
        start_date: startDate,
        end_date: endDate,
    });


    const calculation = calculateData?.data?.data ?? {};

    // Adjust this field if your calculation API uses a different name.
    const rowCount =  calculation?.count ?? 0;
    const sum =  calculation?.sum ?? 0;


    const canCreate = hasCalculated && Number(rowCount) > 0;

    useEffect(() => {
        setHasCalculated(false);
    }, [startDate, endDate]);

    const handleCalculate = () => {
        if (!startDate || !endDate) {
            return;
        }

        if (dayjs(endDate).isBefore(dayjs(startDate), "day")) {
            return;
        }

        setHasCalculated(true);
    };

    const handleCreate = (values) => {
        if (!canCreate) {
            return;
        }

        createNewGroup({
            title: values.title.trim(),
            description: values.description?.trim() || null,
            start_date: startDate,
            end_date: endDate,
        }, {
            onSuccess: (r) => {
                if(r?.data?.data?.id)
                {
                    navigate(sprintf(URLS.admin.refund.group.edit, {id: r?.data?.data?.id}));
                }
                ApiHandleSuccessMessage(r)
            },
            onError: (e) => {
                ApiHandleErrorMessage(e);
            }
        });
    };

    const handleReset = () => {
        setStartDate(null);
        setEndDate(null);
        setHasCalculated(false);
        setGroupTitle("");
        setDescription("");
        form.resetFields();
    };

    return (
        <AdminLayout
            pageTitle="ثبت گروه جدید بازگشت وجه"
            backTo={URLS.admin.refund.dashboard}
        >
            <div
                className="space-y-6 pb-8 text-right"
                dir="rtl"
            >
                {/* Header */}
                <div className="rounded-2xl bg-gradient-to-l from-indigo-700 to-violet-600 p-6 text-white shadow-sm sm:p-8">
                    <div className="flex items-center gap-4">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/15">
                            <Layers3 size={28} />
                        </div>
                        <div>
                            <Title level={3} className="!mb-2 !text-white">
                                ایجاد گروه بازگشت وجه
                            </Title>
                            <Text className="!text-indigo-100">
                                بازه زمانی را انتخاب کنید و پس از محاسبه تعداد
                                درخواست‌ها، اطلاعات گروه را ثبت کنید.
                            </Text>
                        </div>
                    </div>
                </div>

                {/* Date selection */}
                <Card

                    className="!rounded-2xl !bg-white !shadow-sm dark:!bg-[#171a21]"
                >
                    <div className="mb-6 flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-500/10">
                            <CalendarDays
                                size={21}
                                className="text-indigo-600 dark:text-indigo-400"
                            />
                        </div>
                        <div>
                            <Title
                                level={5}
                                className="!mb-1 !text-gray-800 dark:!text-gray-100"
                            >
                                ۱. انتخاب بازه زمانی
                            </Title>
                            <Text className="!text-gray-500 dark:!text-gray-400">
                                تاریخ شروع و پایان درخواست‌های بازگشت وجه را مشخص کنید.
                            </Text>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                تاریخ شروع
                            </label>

                            <DatePicker
                                className="!w-full"
                                size="large"
                                format="YYYY-MM-DD"
                                placeholder="انتخاب تاریخ شروع"
                                value={startDate ? dayjs(startDate).calendar('jalali') : null}
                                onChange={(date) => {
                                    setStartDate(date ? date.calendar('gregory') : null);
                                    setHasCalculated(false);
                                }}
                                disabledDate={(date) =>
                                    endDate
                                        ? date.isAfter(dayjs(endDate), "day")
                                        : false
                                }
                            />
                        </div>

                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                تاریخ پایان
                            </label>
                            <DatePicker
                                className="!w-full"
                                size="large"
                                format="YYYY-MM-DD"
                                placeholder="انتخاب تاریخ پایان"
                                value={endDate ? dayjs(endDate).calendar('jalali') : null}
                                onChange={(date) => {
                                    setEndDate(date ? date.calendar("gregory") : null);
                                    setHasCalculated(false);
                                }}
                                disabledDate={(date) =>
                                    startDate
                                        ? date.isBefore(dayjs(startDate), "day")
                                        : false
                                }
                            />
                        </div>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                        <Button
                            type="primary"
                            size="large"
                            icon={<RotateCcw size={17} />}
                            loading={startDate && endDate && isPendingCalculate}
                            disabled={
                                !startDate ||
                                !endDate ||
                                dayjs(endDate).isBefore(dayjs(startDate), "day")
                            }
                            onClick={handleCalculate}
                        >
                            محاسبه درخواست‌ها
                        </Button>

                        <Button
                            size="large"
                            onClick={handleReset}
                            disabled={isPendingCalculate || isCreating}
                        >
                            پاک کردن اطلاعات
                        </Button>
                    </div>

                </Card>

                {/* Calculation result */}
                {hasCalculated && (
                    <Card

                        className="!rounded-2xl !bg-white !shadow-sm dark:!bg-[#171a21]"
                    >
                        {isPendingCalculate ? (
                            <div className="flex flex-col items-center justify-center gap-3 py-10">
                                <Spin size="large" />
                                <Text className="!text-gray-500 dark:!text-gray-400">
                                    در حال محاسبه درخواست‌های بازگشت وجه...
                                </Text>
                            </div>
                        ) :  (
                            <div className="space-y-5">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10">
                                        <CheckCircle2
                                            size={21}
                                            className="text-emerald-600 dark:text-emerald-400"
                                        />
                                    </div>
                                    <div>
                                        <Title
                                            level={5}
                                            className="!mb-1 !text-gray-800 dark:!text-gray-100"
                                        >
                                            ۲. نتیجه محاسبه
                                        </Title>
                                        <Text className="!text-gray-500 dark:!text-gray-400">
                                            نتیجه بررسی بازه زمانی انتخاب‌شده
                                        </Text>
                                    </div>
                                </div>

                                <div className="rounded-2xl bg-gray-50 p-5 dark:bg-white/5">
                                    <div className="flex items-center justify-between gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                تعداد درخواست‌های یافت شده
                                            </p>
                                            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
                                                {Number(rowCount).toLocaleString("fa-IR")}
                                            </p>
                                        </div>
                                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 dark:bg-indigo-500/10">
                                            مورد
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                جمع مبالغ این درخواست‌ها
                                            </p>
                                            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
                                                {Number(sum).toLocaleString("fa-IR")}
                                            </p>
                                        </div>
                                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 dark:bg-indigo-500/10">
                                            تومان
                                        </div>
                                    </div>
                                </div>

                                {Number(rowCount) > 0 ? (
                                    <>
                                        <Alert
                                            type="success"
                                            showIcon

                                            description="عنوان گروه را وارد کنید. توضیحات اختیاری است."
                                        />

                                        <div className="border-t border-gray-100 pt-5 dark:border-white/10">
                                            <Title
                                                level={5}
                                                className="!mb-5 !text-gray-800 dark:!text-gray-100"
                                            >
                                                ۳. اطلاعات گروه
                                            </Title>

                                            <Form
                                                form={form}
                                                layout="vertical"
                                                onFinish={handleCreate}
                                                requiredMark={false}
                                            >
                                                <Form.Item
                                                    name="title"
                                                    label="عنوان گروه"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            whitespace: true,
                                                            message: "عنوان گروه را وارد کنید.",
                                                        },
                                                        {
                                                            max: 255,
                                                            message: "عنوان نمی‌تواند بیشتر از ۲۵۵ کاراکتر باشد.",
                                                        },
                                                    ]}
                                                >
                                                    <Input
                                                        size="large"
                                                        placeholder="مثلاً بازگشت وجه مهرماه"
                                                        prefix={
                                                            <Layers3
                                                                size={17}
                                                                className="ml-1 text-gray-400"
                                                            />
                                                        }
                                                        disabled={isCreating}
                                                    />
                                                </Form.Item>

                                                <Form.Item
                                                    name="description"
                                                    label="توضیحات (اختیاری)"
                                                >
                                                    <TextArea
                                                        rows={4}
                                                        maxLength={2000}
                                                        showCount
                                                        placeholder="توضیحات تکمیلی درباره این گروه..."
                                                        disabled={isCreating}
                                                    />
                                                </Form.Item>

                                                <div className="flex justify-end">
                                                    <Button
                                                        type="primary"
                                                        htmlType="submit"
                                                        size="large"
                                                        icon={<Plus size={17} />}
                                                        loading={isCreating}
                                                    >
                                                        ثبت گروه بازگشت وجه
                                                    </Button>
                                                </div>
                                            </Form>
                                        </div>
                                    </>
                                ) : (
                                    <Alert
                                        type="warning"
                                        showIcon
                                        message="درخواستی برای گروه‌بندی پیدا نشد."
                                        description="برای این بازه زمانی رکوردی وجود ندارد. بازه دیگری انتخاب کنید."
                                    />
                                )}
                            </div>
                        )}
                    </Card>
                )}
            </div>
        </AdminLayout>
    );
}

export default AdminRefoundGroupCreate;